import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { isAdminModuleEnabled } from "../config/features";
import AdminAccessDenied from "../pages/admin/AdminAccessDenied";
import { resolveEntryPointRoute, usePlatformContext } from "../context/PlatformContext";
import { logActivity } from "../utils/activity";
import ErrorCard from "./ErrorCard";
import AccountDisabled from "../pages/AccountDisabled";
import BlockingActionModal from "./BlockingActionModal";
import { useAuth } from "../context/AuthContext";
import { getPlatformRole, isAdminRole } from "../utils/platformRole";

export default function ProtectedRoute({ children, requireAdmin = false }) {
  const { user: authUser, isLoading, sessionExpired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: platformLoading, status, session, uiHints } = usePlatformContext();
  const role = getPlatformRole();
  const [showSoftBlock, setShowSoftBlock] = useState(false);
  const softBlockShownRef = useRef(false);
  const currentPath = location?.pathname || "";
  const token = (() => {
    try {
      const raw = localStorage.getItem("myv_token");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  })();
  const expiresAt = (() => {
    try {
      const raw = localStorage.getItem("myv_token_expires_at");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  })();
  const isExpired = typeof expiresAt === "number" && Date.now() > expiresAt;

  const softBlockRequired = useMemo(() => {
    return (
      session?.profileCompleted === false &&
      !session?.forceAction &&
      !session?.requiresPayment &&
      uiHints?.recommendedAction === "COMPLETE_PROFILE"
    );
  }, [session?.forceAction, session?.profileCompleted, session?.requiresPayment, uiHints?.recommendedAction]);

  useEffect(() => {
    if (!isLoading && !authUser) {
      logActivity("protected_overlay_open", { expired: sessionExpired });
    }
  }, [isLoading, authUser, sessionExpired]);

  useEffect(() => {
    if (!softBlockRequired) {
      setShowSoftBlock(false);
      return;
    }
    if (currentPath === "/profile/setup") return;
    if (softBlockShownRef.current) return;
    softBlockShownRef.current = true;
    setShowSoftBlock(true);
  }, [currentPath, softBlockRequired]);

  if (isLoading || platformLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="card w-full max-w-md p-6">
          <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
          <div className="mt-4 h-3 w-full animate-pulse rounded bg-slate-200" />
          <div className="mt-2 h-3 w-2/3 animate-pulse rounded bg-slate-200" />
          <div className="mt-6 h-10 w-full animate-pulse rounded bg-slate-200" />
        </div>
      </div>
    );
  }

  if (status === "disabled") {
    return <AccountDisabled />;
  }

  if (status === "error" || status === "forbidden") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <ErrorCard />
      </div>
    );
  }

  if (status === "missing") {
    return <Navigate to="/login" replace />;
  }

  if (token && !isExpired) {
    const requiresPayment = session?.requiresPayment === true;
    const forcedRoute = resolveEntryPointRoute(session?.forceAction);
    if (requiresPayment && currentPath !== "/billing") {
      return <Navigate to="/billing" replace />;
    }
    if (forcedRoute && currentPath !== forcedRoute) {
      return <Navigate to={forcedRoute} replace />;
    }
    if (requireAdmin && !isAdminModuleEnabled({ role }) && !isAdminRole(role)) {
      return <AdminAccessDenied />;
    }
    return (
      <>
        {children}
        <BlockingActionModal
          isOpen={showSoftBlock}
          onComplete={() => {
            setShowSoftBlock(false);
            if (currentPath !== "/profile/setup") {
              navigate("/profile/setup", { replace: true });
            }
          }}
          onCancel={() => setShowSoftBlock(false)}
        />
      </>
    );
  }

  try {
    localStorage.setItem(
      "myv_session_expired_message",
      "Your session has expired. Please sign in again."
    );
  } catch (error) {
    // no-op
  }
  return <Navigate to="/login" replace />;
}
